/**
 * Utility for fetching child items from GROUP or COLLECTION nodes
 */

import axios from "axios";

import { ItemsResponse } from "./types";

import {
  GroupResponse,
  CollectionResponse,
  LinkResponse,
} from "@/utils/FetchAssetDetails/types";
import { ATOM_NODE_URL, ATOM_HEADERS } from "@/constants/api";

/**
 * Fetches child items (rails/collections) from a GROUP, COLLECTION, or LINK node
 *
 * @remarks
 * This function uses the ATOM API's AQL (ATOM Query Language) `represent` parameter
 * to expand the items relationship. When fetching the home page (`/home`), you'll get
 * a GROUP node. Use this function to get the rails (COLLECTION nodes) that appear on
 * that page. You can then use this same function to fetch the individual items within
 * each rail.
 *
 * @param nodeOrId - Either a GroupResponse/CollectionResponse/LinkResponse object or a node UUID string
 * @returns Promise that resolves to an ItemsResponse containing the child items
 *
 * @example
 * ```typescript
 * // First, fetch the home page group
 * const homeGroup = await fetchAssetDetails("/home");
 *
 * // Then fetch all the rails on the home page
 * const rails = await fetchChildItems(homeGroup);
 * console.log(`Found ${rails.items.length} rails`);
 *
 * // Fetch items from a specific rail
 * const firstRail = rails.items[0];
 * const railItems = await fetchChildItems(firstRail);
 * console.log(`First rail contains ${railItems.items.length} items`);
 *
 * // Or fetch by UUID directly
 * const items = await fetchChildItems("32849634-d3d3-11e9-bbc6-d7a405d0af9d");
 * ```
 *
 * @throws Will throw an error if the network request fails
 */
const fetchChildItems = (
  nodeOrId: GroupResponse | CollectionResponse | LinkResponse | string,
): Promise<ItemsResponse> => {
  // Determine the node ID
  const nodeId = typeof nodeOrId === "string" ? nodeOrId : nodeOrId.id;

  // Construct the URL using the node endpoint with represent parameter
  // This uses AQL (ATOM Query Language) to expand the items relationship
  const url = `${ATOM_NODE_URL}${nodeId}`;

  return axios
    .get(url, {
      headers: ATOM_HEADERS,
      params: {
        represent: "(items)", // AQL: expand the items collection
      },
    })
    .then((response) => {
      // Extract items from the relationships object
      const items = response.data.relationships?.items?.data || [];

      return {
        items,
        _links: response.data.relationships?.items?.links,
      };
    });
};

export { fetchChildItems };
